/*!
 * MTAppjQuery.js
 *
 * Copyright (c) bit part LLC
 * https://bit-part/
 *
 * Since:   2010-06-22
 * Version: 3.6.0
 * Update:  2025-10-16
 *
 */
import { AssetField } from './methods/AssetField.js';
import { ImageField } from './methods/ImageField.js';
import { columnGroup } from './methods/columnGroup.js';
import { columnGroupSettingUI } from './methods/columnGroupSettingUI.js';
import { condition } from './methods/condition.js';
import { copyToClipboard } from './methods/copyToClipboard.js';
import { csvEditor } from './methods/csvEditor.js';
import { customize } from './methods/customize.js';
import { dataLabelCustomize } from './methods/dataLabelCustomize.js';
import { debug } from './methods/debug.js';
import { deepCopy } from './methods/deepCopy.js';
import { duplicate } from './methods/duplicate.js';
import { duplicateContent } from './methods/duplicateContent.js';
import { errorMessage } from './methods/errorMessage.js';
import { fieldSort } from './methods/fieldSort.js';
import { getCategoryName } from './methods/getCategoryName.js';
import { getCookie } from './methods/getCookie.js';
import { getContentDataFields } from './methods/getContentDataFields.js';
import { getContentFieldIdByLabel } from './methods/getContentFieldIdByLabel.js';
import { getCurrentContentType } from './methods/getCurrentContentType.js';
import { getDataApiUrl } from './methods/getDataApiUrl.js';
import { getPageHeight } from './methods/getPageHeight.js';
import { getPageScroll } from './methods/getPageScroll.js';
import { getPublicUrl } from './methods/getPublicUrl.js';
import { isPlainObject } from './methods/isPlainObject.js';
import { loadingImage } from './methods/loadingImage.js';
import { makeField } from './methods/makeField.js';
import { makeWidget } from './methods/makeWidget.js';
import { modalConfirm } from './methods/modalConfirm.js';
import { modalMsg } from './methods/modalMsg.js';
import { moveToWidget } from './methods/moveToWidget.js';
import { msg } from './methods/msg.js';
import { MultiFieldPro } from './methods/MultiFieldPro.js';
import { numberFormat } from './methods/numberFormat.js';
import { objectSort } from './methods/objectSort.js';
import { observeCategoryChange, observeCategoryChangeCheck } from './methods/observeCategoryChange.js';
import { SlideEditor } from './methods/SlideEditor.js';
import { SlideEditor as SlideEditor2 } from './methods/SlideEditor2.js';
import { randomString } from './methods/randomString.js';
import { rebuildTemplate } from './methods/rebuildTemplate.js';
import { removeHost } from './methods/removeHost.js';
import { setCookie } from './methods/setCookie.js';
import { showHint } from './methods/showHint.js';
import { sidebarCollapse } from './methods/sidebarCollapse.js';
import { sortDisabled } from './methods/sortDisabled.js';
import { submitButtonControl } from './methods/submitButtonControl.js';
import { tabs } from './methods/tabs.js';
import { temporaryId } from './methods/temporaryId.js';
import { tidySeparator } from './methods/tidySeparator.js';
import { toInt } from './methods/toInt.js';
import { toJSONString } from './methods/toJSONString.js';
import { zeroPad } from './methods/zeroPad.js';
import { initBody } from './methods/initBody.js';
import ScheduleField from './components/ScheduleField.svelte';

// Deprecated
import { assetField as deprecated_assetField } from './methods/deprecated/assetField.js';
import { overlayEditor as deprecated_overlayEditor } from './methods/deprecated/overlayEditor.js';
import { multiField as deprecated_multiField_vue } from './methods/deprecated/multiField.js';
import { multiField as deprecated_multiField_svelte } from './methods/deprecated/multiField-v1/multiField.js';
import { defaultFieldBlocks } from './multiFieldPro/defaultFieldBlocks.js';
import { MTAppJSONTable } from './methods/deprecated/MTAppJSONTable.js';
import { MTAppListing } from './methods/deprecated/MTAppListing.js';
import { mount } from 'svelte';

// @ts-ignore
const mtappVars = window.mtappVars;
// @ts-ignore
const tinymce = window.tinymce;
// @ts-ignore
const MTRichTextEditor = window.MTRichTextEditor

// mtappVars が定義されていない場合はエラーを出して処理を中断
if (typeof mtappVars === 'undefined') {
    throw new Error('mtappVars is required');
}
mtappVars.__system = {
    mtappJqueryReady: false,
    uniqueId: `mt${randomString(8)}`,
    multiFieldErrors: {}
};

class MTAppjQuery {
    constructor() {
        // 既に初期化済みの場合は処理を中断
        if (mtappVars.__system.mtappJqueryReady === true) {
            return;
        }

        // body に対しての初期化処理
        this.initBody();
        // サイドバーの開閉ボタンを追加
        this.sidebarCollapse();
        // 選択されたカテゴリを監視する（記事編集画面）
        this.observeCategoryChange();
        // 保存ボタンコントロールを初期化
        this.submitButtonControl('init');
        // コンテンツタイプのフィールドを取得しておく
        this.getContentDataFields();

        // Deprecated
        this.deprecated = {
            assetField: deprecated_assetField,
            overlayEditor: deprecated_overlayEditor
        };

        // 管理画面のURLを取得
        mtappVars.adminScript = location.href.replace(/\?.*/, '');

        if (window.top === window || typeof window.mtappDialogEdit !== 'undefined') {
            // input のカスタムイベントを設定
            mtappVars.eventInput = new CustomEvent('input');

            // .mt-dialog の表示状態を監視するイベントを設定
            mtappVars.MTAppObsDialog = {
                dialog: null,
                open: false,
                interval: 800,
                callbackTargetSelector: '',
                obs: function () {
                    if (mtappVars.MTAppObsDialog.dialog === null) {
                        mtappVars.MTAppObsDialog.dialog = $('div.mt-dialog,div.mt-modal').not('#mtapplisting-dialog');
                    }
                    const timeoutId = setTimeout(mtappVars.MTAppObsDialog.obs, mtappVars.MTAppObsDialog.interval);
                    // ダイアログが開いたことを記録
                    if (mtappVars.MTAppObsDialog.dialog.is(':visible') && mtappVars.MTAppObsDialog.open === false) {
                        mtappVars.MTAppObsDialog.open = true;
                    }
                    // ダイアログが開いた後に閉じられたとき
                    else if (mtappVars.MTAppObsDialog.dialog.is(':hidden') && mtappVars.MTAppObsDialog.open === true) {
                        mtappVars.MTAppObsDialog.open = false;
                        if (mtappVars.MTAppObsDialog.callbackTargetSelector) {
                            $(mtappVars.MTAppObsDialog.callbackTargetSelector).trigger('MTAppDialogClosed');
                        }
                        clearTimeout(timeoutId);
                    }
                }
            };

            // オーバーレイ・リッチエディタを初期化
            this.deprecated.overlayEditor.init();
        }

        // カスタムイベントの発火
        mtappVars.MTAppjQueryReady = true;
        document.dispatchEvent(new Event('MTAppjQueryReady'));
    }

    columnGroup = columnGroup;
    columnGroupSettingUI = columnGroupSettingUI;
    condition = condition;
    copyToClipboard = copyToClipboard;
    customize = customize;
    dataLabelCustomize = dataLabelCustomize;
    debug = debug;
    deepCopy = deepCopy;
    defaultFieldBlocks = defaultFieldBlocks;
    duplicate = duplicate;
    duplicateContent = duplicateContent;
    errorMessage = errorMessage;
    fieldSort = fieldSort;
    getCategoryName = getCategoryName;
    getContentDataFields = getContentDataFields;
    getContentFieldIdByLabel = getContentFieldIdByLabel;
    getCookie = getCookie;
    getCurrentContentType = getCurrentContentType;
    getDataApiUrl = getDataApiUrl;
    getPageHeight = getPageHeight;
    getPageScroll = getPageScroll;
    getPublicUrl = getPublicUrl;
    initBody = initBody;
    isPlainObject = isPlainObject;
    loadingImage = loadingImage;
    makeField = makeField;
    makeWidget = makeWidget;
    modalConfirm = modalConfirm;
    modalMsg = modalMsg;
    moveToWidget = moveToWidget;
    msg = msg;
    numberFormat = numberFormat;
    objectSort = objectSort;
    observeCategoryChange = observeCategoryChange;
    observeCategoryChangeCheck = observeCategoryChangeCheck;
    randomString = randomString;
    rebuildTemplate = rebuildTemplate;
    removeHost = removeHost;
    setCookie = setCookie;
    showHint = showHint;
    sidebarCollapse = sidebarCollapse;
    sortDisabled = sortDisabled;
    submitButtonControl = submitButtonControl;
    tabs = tabs;
    temporaryId = temporaryId;
    tidySeparator = tidySeparator;
    toInt = toInt;
    toJSONString = toJSONString;
    zeroPad = zeroPad;

    assetField = (id, type = 'image') => {
        return new AssetField(id, type);
    };

    multiField = (options = {}) => {
        if (typeof options.version === 'undefined' || options.version === '' || options.version === 3) {
            options.version = 3;
            return new MultiFieldPro(options, mtappVars);
        } else if (options.version === 2) {
            if (options.useVueJs) {
                return deprecated_multiField_vue(options);
            } else {
                return new MultiFieldPro(options, mtappVars);
            }
        } else {
            if (options.useVueJs) {
                return deprecated_multiField_vue(options);
            } else {
                return deprecated_multiField_svelte(options);
            }
        }
    };

    scheduleField = (target, options, data, translate) => {
        return mount(ScheduleField, {
            target: target,
            props: {
                mtappVars: mtappVars,
                data: data,
                options: options,
                translate: translate
            }
        });
    };

    slideEditor = (target, options = {}, preset = '') => {
        if (tinymce) {
            return new SlideEditor(target, options, preset);
        } else if (MTRichTextEditor) {
            return new SlideEditor2(target, options, preset);
        }
    };

    // v3.0.0 で削除
    templateListCustomize() {
        console.warn('mtapp.templateListCustomize() は v3.0.0 で削除されました。');
    }
}

// スライドエディタを初期化
if (MTRichTextEditor) {
    SlideEditor2.init();
} else if (tinymce) {
    SlideEditor.init();
}

// アセットフィールドを初期化
AssetField.init();
// ImageField.init()は各コンポーネントで遅延初期化されるため、ここでは呼び出さない

// mtapp インスタンスを作成
// @ts-ignore
window.mtapp = new MTAppjQuery(mtappVars);

// ImageFieldをグローバルに公開
window.mtapp.ImageField = ImageField;

(function ($, mtappVars) {
    const methods = {
        csvEditor: csvEditor,

        /**
         * .mtapp('tooltip', options)
         *
         * 指定した要素にマウスオーバーするとカーソルに追随するツールチップを表示します。
         * html オプション、title 属性、alt 属性の値の優先順位でツールチップで表示します。
         *
         * Updated: 2018/06/22
         *
         * @param {Object} options
         * @param {String} options.html ツールチップに表示させるテキスト（HTML）
         * @param {String} options.className ツールチップにセットされるクラス名
         * @param {Boolean} options.remove `true` をセットすると適用した `tooltip` を削除します。
         * @returns {*}
         */
        tooltip: function (options) {
            const op = $.extend(
                {
                    html: '',
                    className: '',
                    remove: false
                },
                options
            );

            return this.each(function () {
                if (op.remove) {
                    $(this).off('mouseenter.mtappTooltip').off('mouseleave.mtappTooltip').off('mousemove.mtappTooltip');
                    return;
                }

                if (op.text && op.html === '') {
                    op.html = op.text;
                }

                if (typeof op.className !== 'string' || op.className === '') {
                    op.className = 'bg-light';
                }

                const $this = $(this);
                const $tooltip = $('#mtapp-tooltip').addClass(op.className);
                let target, tipText;

                if (op.html !== '') {
                    tipText = op.html;
                } else {
                    (target = this.title ? 'title' : 'alt'), (tipText = $this.attr(target));
                }

                $this.on({
                    'mouseenter.mtappTooltip': function (e) {
                        if (op.html === '') {
                            $this.attr(target, '');
                        }
                        $tooltip
                            .stop(true, true)
                            .fadeIn('fast')
                            .html(tipText)
                            .css({
                                position: 'absolute',
                                top: e.pageY - 20,
                                left: e.pageX + 20
                            });
                    },
                    'mouseleave.mtappTooltip': function () {
                        if (op.html === '') {
                            $this.attr(target, tipText);
                        }
                        $tooltip.fadeOut('fast');
                    },
                    'mousemove.mtappTooltip': function (e) {
                        $tooltip.css({
                            top: e.pageY - 20,
                            left: e.pageX + 20
                        });
                    }
                });
            });
        },

        /**
         * .mtapp('enableEdit', options)
         *
         * input:text, textarea を readonly にする
         *
         * Updated: 2018/06/06
         *
         * @param {Object} options
         * @param {Object} options.text ボタンのテキスト
         * @returns {*}
         */
        enableEdit: function (options) {
            const op = $.extend({ text: '編集' }, options);

            return this.each(function () {
                if (this.value === '') {
                    return;
                }
                const $this = $(this);
                $this.wrap('<div class="form-inline"></div>');
                $this.prop('readonly', true);
                const $btn = $('<button class="btn btn-default ml-2" type="button">' + op.text + '</button>').on('click', function () {
                    $(this).hide().prev().prop('readonly', false).parent().removeClass('form-inline');
                    return false;
                });
                $this.after($btn);
            });
        },

        /**
         * .mtapp('inlineEdit', options)
         *
         * input:text, textarea を readonly にする
         *
         * Updated: 2018/06/06
         *
         * @param {Object} options
         * @param {Object} options.edit インラインエディットモードを切り替えるボタンのテキスト
         * @param {Boolean} options.force 常にインラインエディットモードにする場合(true)
         * @returns {*}
         */
        inlineEdit: function (options) {
            const op = $.extend(
                {
                    edit: '編集',
                    force: false
                },
                options
            );

            return this.each(function () {
                const $this = $(this);
                const $btn = $('<button class="btn btn-default ml-3" type="button">' + op.edit + '</button>').on('click', function () {
                    $(this).hide().prev().show().addClass('edited').prev().hide();
                    return false;
                });
                let thisValue = $this.val();
                if (op.force || thisValue !== '') {
                    thisValue = thisValue !== '' ? thisValue : '...';
                    $this
                        .before('<span>' + thisValue + '</span>')
                        .after($btn)
                        .hide();
                }
            });
        },

        /**
         * .mtapp('tabToSpace', options)
         *
         * `textarea` でタブキーが押されたときに、スペースなどの文字列を挿入します。
         *
         * Updated: 2018/06/22
         *
         * @param {Object} options
         * @param {String} options.text タブキーが押されたときに入力される文字列。初期値は半角スペース4つ。
         * @returns {*}
         */
        tabToSpace: function (options) {
            const op = $.extend({ text: '    ' }, options);

            return this.each(function () {
                $(this).on('keydown', function (e) {
                    const keycode = e.which || e.keyCode;
                    if (keycode == 9) {
                        $(this).insertAtCaret(op.text);
                        return false;
                    }
                });
            });
        },

        /**
         * .mtapp('removeVal')
         *
         * 指定した `input:text` `textarea` に入力された値をクリアするボタンを付けます。
         *
         * Updated: 2018/06/22
         *
         * @returns {*}
         */
        removeVal: function () {
            return this.each(function () {
                const $this = $(this).css('padding-right', '16px').wrap('<div class="mtapp-remove-val" style="position:relative;" />');
                const $wrap = $this.parent();
                const outerH = $wrap.outerHeight();
                const posTop = outerH / 2 - 7;
                $wrap
                    .append(
                        '<img class="mtapp-remove-val-btn" alt="" src="' +
                            mtappVars.static_plugin_path +
                            'images/cancel-gray.png" style="top: ' +
                            posTop +
                            'px;" />'
                    )
                    .children('img')
                    .on('click', function () {
                        $this.val('').trigger('focus');
                    });
            });
        },

        /**
         * .mtapp('showHint', options) - mtapp.showHint() の jQuery プラグイン版
         *
         * @param options
         * @returns {false}
         */
        showHint: function (options = {}) {
            return this.each(function () {
                showHint(this, options);
            });
        },

        /**
         * .mtapp('fieldSplit')
         *
         * 1つのフィールドを分割して複数の値をカンマ区切りなどで保存出来るようになります。
         *
         * Updated: 2018/06/22
         *
         * @param {Object} options
         * @param {Number} options.splitCount 分割する数
         * @param {String} options.separator 区切り文字列
         * @param {Array} options.placeholders プレースホルダーの配列
         * @param {Array} options.styles style属性を追加する場合はその配列
         * @param {Array} options.classes クラスを追加する場合はその配列
         * @param {Boolean} options.debug trueを設定すると元のフィールドを表示します。
         * @returns {*}
         */
        fieldSplit: function (options) {
            const op = $.extend(
                {
                    splitCount: 2,
                    separator: ',',
                    placeholders: [],
                    styles: [],
                    classes: [],
                    debug: false
                },
                options
            );

            return this.each(function () {
                const $self = $(this);
                const separator = op.separator;
                const splitCount = op.splitCount > 1 ? op.splitCount : 2;
                const selfVal = $self.val() ? $self.val().split(op.separator) : [];

                if (!op.debug) {
                    $self.hide();
                }

                const input = [];
                let value = '',
                    placeholders = '',
                    styles = '',
                    classes = '';
                op.placeholders = op.placeholder ? op.placeholder : op.placeholders;
                for (let i = 0; i < splitCount; i++) {
                    value = selfVal[i] ? selfVal[i] : '';
                    placeholders = op.placeholders[i] ? op.placeholders[i] : '';
                    styles = op.styles[i] ? op.styles[i] : '';
                    classes = op.classes[i] ? op.classes[i] : '';
                    input.push(
                        '<div class="' +
                            classes +
                            '"><input type="text" class="form-control mtapp-fieldsplit" value="' +
                            value +
                            '" placeholder="' +
                            placeholders +
                            '" style="' +
                            styles +
                            '" /></div>'
                    );
                }
                const $div = $('<div class="row MTAppFieldSplit">' + input.join('') + '</div>')
                    .find('.form-control')
                    .each(function () {
                        $(this).on({
                            blur: function () {
                                const values = [];
                                $(this)
                                    .closest('.MTAppFieldSplit')
                                    .find('.mtapp-fieldsplit')
                                    .each(function () {
                                        values.push($(this).val());
                                    });
                                $self.val(values.join(separator));
                            },
                            keydown: function (e) {
                                if (e.which == 13) {
                                    const values = [];
                                    $(this)
                                        .closest('.MTAppFieldSplit')
                                        .find('.mtapp-fieldsplit')
                                        .each(function () {
                                            values.push($(this).val());
                                        });
                                    $self.val(values.join(separator));
                                }
                            }
                        });
                    })
                    .end();
                $self.after($div);
            });
        },

        /**
         * 今日、明日、明後日をワンクリックで入力できます。
         *
         * Updated: 2018/06/28
         *
         * @returns {*}
         */
        dateAssist: function () {
            const d = new Date();
            const buttons =
                '<div class="btn-group d-block mt-2" role="group">' +
                '<button type="button" class="btn btn-default" data-days="0">今日</button>' +
                '<button type="button" class="btn btn-default" data-days="1">明日</button>' +
                '<button type="button" class="btn btn-default" data-days="2">明後日</button>' +
                '</div>';

            const getDateItem = function (ms) {
                const d = new Date(ms);
                return d.getFullYear() + '-' + zeroPad(d.getMonth() + 1, 2) + '-' + zeroPad(d.getDate(), 2);
            };

            return this.each(function () {
                const $this = $(this);
                $this
                    .after(buttons)
                    .next()
                    .on('click', '.btn', function () {
                        const days = $(this).attr('data-days') - 0;
                        const ms = d.getTime() + days * 24 * 60 * 60 * 1000;
                        $this.val(getDateItem(ms));
                    });
            });
        },

        /**
         * 全角数字を半角に変換、半角数字以外の文字を削除、最小値・最大値の設定などができます。
         *
         * Updated: 2018/06/28
         *
         * @param {Object} options
         * @param {Number} options.min 入力できる最小値を指定します。
         * @param {Number} options.max 入力できる最大値を指定します。
         * @param {Number} options.zeroPad ゼロパディングする場合の桁数を指定します。
         * @returns {*}
         */
        numChecker: function (options) {
            const op = $.extend(
                {
                    min: null,
                    max: null,
                    zeroPad: 0
                },
                options
            );

            return this.each(function () {
                const $this = $(this);
                const $alert = $this
                    .before('<div class="alert alert-danger mtapp-numChecker" role="alert" style="display: none;"></div>')
                    .prev();

                $this
                    .on('blur', function () {
                        let num = '' + $(this).val().trim();
                        num = toInt(num, true);
                        $this.val(num);
                        if (num === '') {
                            return true;
                        }

                        num = Number(num);

                        if (typeof num !== 'number' || isNaN(num)) {
                            $alert.text('数値以外の文字が含まれています。').show();
                            return;
                        } else {
                            $alert.hide();
                        }

                        if (typeof op.min === 'number' && typeof op.max === 'number') {
                            if (num < op.min || op.max < num) {
                                $alert
                                    .html(
                                        '入力できる値は<strong class="mx-1">' +
                                            op.min +
                                            '</strong>から<strong class="mx-1">' +
                                            op.max +
                                            '</strong>までです。'
                                    )
                                    .show();
                            } else {
                                $alert.hide();
                            }
                        } else if (typeof op.min === 'number') {
                            if (num < op.min) {
                                $alert.html('入力できる最小値は<strong class="mx-1">' + op.min + '</strong>までです。').show();
                            } else {
                                $alert.hide();
                            }
                        } else if (typeof op.max === 'number') {
                            if (op.max < num) {
                                $alert.html('入力できる最大値は<strong class="mx-1">' + op.max + '</strong>までです。').show();
                            } else {
                                $alert.hide();
                            }
                        }

                        if (op.zeroPad !== 0) {
                            op.zeroPad = op.zeroPad - 0;
                            num = zeroPad(num, op.zeroPad);
                        }

                        $this.val(num);
                    })
                    .trigger('blur');
            });
        },

        /**
         * 入力された数値から税込み価格、税抜き価格を算出します。
         *
         * Updated: 2020/01/31
         *
         * @param {Object} options
         * @param {Number} options.rate 消費税率（例）5%の時は0.05と書く
         * @param {String} options.rounding 端数処理 => floor（切り捨て）、ceil（切り上げ）、round（四捨五入）

         * @returns {*}
         */
        taxAssist: function (options) {
            const op = $.extend(
                {
                    rate: 0.1,
                    rounding: 'floor'
                },
                options
            );

            const buttons =
                '<div class="btn-group d-block mt-2" role="group">' +
                '<button type="button" class="btn btn-default taxes_included">税込み</button>' +
                '<button type="button" class="btn btn-default after_taxes">税抜き</button>' +
                '<button type="button" class="btn btn-default" disabled>リセット</button>' +
                '</div>';

            return this.each(function () {
                const $this = $(this);

                $this
                    .after(buttons)
                    .next()
                    .on('click', 'button', function () {
                        const $button = $(this);
                        if ($button.hasClass('taxes_included')) {
                            $this.data('original', $this.val());
                            $button.removeClass('btn-default').addClass('btn-dark');
                            $button.next().prop('disabled', true);
                            $button.next().next().prop('disabled', false);
                            const val = Number($this.val()) * (1 + op.rate);
                            $this.val(rounding(val, op.rounding));
                        } else if ($button.hasClass('after_taxes')) {
                            $this.data('original', $this.val());
                            $button.removeClass('btn-default').addClass('btn-dark');
                            $button.prev().prop('disabled', true);
                            $button.next().prop('disabled', false);
                            const val = Number($this.val()) / (1 + op.rate);
                            $this.val(rounding(val, op.rounding));
                        } else {
                            $this.val($this.data('original'));
                            $button.siblings().each(function () {
                                $(this).prop('disabled', false).removeClass('btn-dark').addClass('btn-default');
                            });
                        }
                        $button.prop('disabled', true);
                    });
            });

            function rounding(num, roundingType) {
                switch (roundingType) {
                    case 'floor':
                        num = Math.floor(num);
                        break;
                    case 'ceil':
                        num = Math.ceil(num);
                        break;
                    case 'round':
                        num = Math.round(num);
                        break;
                }
                return num;
            }
        },

        /**
         * フィールドに最大文字数を設定します
         *
         * Updated: 2018/08/10
         *
         * @param {Object} options
         * @param {Object} options.l10n 言語設定
         * @param {Number} options.maxLength The maxLength option is required. You have to set not less than 1.
         * @returns {*}
         */
        maxLength: function (options) {
            const op = $.extend(
                {
                    l10n: null,
                    maxLength: 0
                },
                options
            );

            if (op.maxLength < 1) {
                return;
            }
            /* ==================================================
          L10N
      ================================================== */
            const l10n = {};
            if (mtappVars.language === 'ja') {
                l10n.characters = '文字';
                l10n.maximumCharacters = '最大文字数';
                l10n.remainingCharacters = '残り文字数';
                l10n.overCharacters = '最大文字数を[_1]文字超えています';
            } else {
                l10n.characters = '';
                l10n.maximumCharacters = 'Maximum characters';
                l10n.remainingCharacters = 'Remaining characters';
                l10n.overCharacters = '[_1] characters over';
            }
            if (op.l10n) {
                $.extend(l10n, op.l10n);
            }
            /*  L10N  */

            return this.each(function () {
                const maxLength = op.maxLength;

                $(this).after('<div class="valid-feedback"></div>' + '<div class="invalid-feedback"></div>');

                const $valid = $(this).next();
                const $invalid = $valid.next();

                $(this)
                    .on('keyup', function () {
                        const $this = $(this);
                        const count = $this.val().length;
                        if (count > maxLength) {
                            $this.removeClass('is-valid').addClass('is-invalid');
                            $invalid.text(statusText(maxLength, count, l10n));
                            mtapp.submitButtonControl('hasError');
                        } else {
                            $valid.text(statusText(maxLength, count, l10n));
                            $this.removeClass('is-invalid').addClass('is-valid');
                            mtapp.submitButtonControl('submittable');
                        }
                    })
                    .trigger('keyup');
            });

            function statusText(maxLength, count, l10n) {
                if (!count) {
                    return l10n.maximumCharacters + ' : ' + maxLength + l10n.characters;
                } else if (count <= maxLength) {
                    return l10n.remainingCharacters + ' : ' + (maxLength - count) + l10n.characters;
                } else if (count > maxLength) {
                    const overCount = count - maxLength;
                    return l10n.overCharacters.replace(/\[_1\]/, overCount);
                }
            }
        },

        /**
         * テキストフィールドを複数選択チェックボックスにします。
         *
         * Updated: 2021/02/09
         *
         * @param {Object} options
         * @param {Object} options.l10n
         * @param {Object} options.label カンマ区切りの文字列か{'key1':'value1','key2':'value2'}のハッシュ
         * @param {Object} options.maxCount チェックできる最大値を設定
         * @param {Object} options.maxCountMessage チェックできる最大値を超えたときにエラーメッセージ
         * @param {Object} options.insert 'before' or 'after'
         * @param {Object} options.add ユーザーがチェックボックスを追加できるようにする場合はtrue
         * @param {Object} options.sort 'ascend'（昇順）,'descend'（降順）
         * @param {Object} options.debug 'hide' or 'show' 元のテキストフィールドを非表示にするか否か
         * @returns {*}
         */
        multiCheckbox: function (options) {
            const op = $.extend(
                {
                    l10n: null,
                    label: '',
                    maxCount: 999999999,
                    maxCountMessage: '',
                    add: false,
                    sort: '',
                    debug: false
                },
                options
            );

            const l10n = {};
            if (mtappVars.language === 'ja') {
                l10n.maxCountMessage = '選択出来る上限数を超えました。';
                l10n.addItem = '項目を追加';
                l10n.add = '追加';
            } else {
                l10n.maxCountMessage = 'Exceed the check limit.';
                l10n.addItem = 'Add an item';
                l10n.add = 'Add';
            }
            if (op.l10n) {
                $.extend(l10n, op.l10n);
            }

            return this.each(function () {
                const $this = $(this);

                // 最大文字数表記を非表示にする
                $this
                    .next()
                    .filter(function () {
                        return this.innerHTML.includes('最大文字数');
                    })
                    .hide();

                const savedItems = this.value ? tidySeparator(this.value, ',').split(',') : [];
                const labelType = typeof op.label === 'object' ? (Array.isArray(op.label) ? 'array' : 'object') : typeof op.label;
                const labels = [];
                const items = [];
                const containerId = temporaryId();
                const $container = $this.after('<div id="' + containerId + '"></div>').next();

                if (!op.debug) {
                    $this.hide();
                }

                if (labelType === 'object') {
                    if (op.sort === '') {
                        for (let key in op.label) {
                            labels.push(makeLabel(op.label[key], key));
                        }
                    } else {
                        const keys = sortHashKey(op.label, op.sort);
                        for (let i = 0, n = keys.length; i < n; i++) {
                            labels.push(makeLabel(op.label[keys[i]], keys[i]));
                        }
                    }
                } else if (labelType === 'string' || labelType === 'array') {
                    switch (labelType) {
                        case 'string':
                            if (op.label === '' && $this.attr('title')) {
                                items.push(...tidySeparator($this.attr('title')).split(','));
                            } else {
                                items.push(...tidySeparator(op.label).split(','));
                            }
                            op.label = [...items];
                            break;
                        case 'array':
                            items.push(...op.label);
                            break;
                    }
                    if (savedItems.length > 0 && op.add) {
                        for (let i = 0, n = savedItems.length; i < n; i++) {
                            if (!op.label.includes(savedItems[i])) {
                                items.push(savedItems[i]);
                            }
                        }
                    }
                    if (op.sort === 'ascend') {
                        items.sort();
                    } else if (op.sort === 'descend') {
                        items.sort().reverse();
                    }
                    for (let i = 0, n = items.length; i < n; i++) {
                        labels.push(makeLabel(items[i], items[i]));
                    }
                    if (op.add) {
                        labels.push(`
                <div id="${containerId}-group" class="input-group mt-3">
                    <input id="${containerId}-add" class="mcb-add-item form-control" type="text" value="" style="max-width: 300px" placeholder="${l10n.addItem}">
                    <button id="${containerId}-add-button" class="btn btn-default mcb-add-item-button" type="button" data-target-selector="#${containerId}-add">
                        <svg role="img" class="mt-icon--success"><title>追加</title><use xlink:href="${mtappVars.static_path}/images/sprite.svg#ic_add"></use></svg>
                        ${l10n.add}
                    </button>
                </div>
            `);
                    }
                }

                $container.html(labels.join('')).on('click', 'input:checkbox', function () {
                    const checkValues = [];
                    const checkedCount = $container.find(':checked').length;
                    if (checkedCount > op.maxCount) {
                        alert(op.maxCountMessage || l10n.maxCountMessage);
                        return false;
                    }
                    $container.find(':checked').each(function () {
                        checkValues.push(this.value);
                    });
                    $this.val(checkValues.join(','));
                });

                if (savedItems.length > 0) {
                    $container.find(':checkbox').each(function () {
                        if (savedItems.includes(this.value)) {
                            $(this).prop('checked', true);
                        }
                    });
                }

                if (op.add) {
                    const $inputGroup = $('#' + containerId + '-group');
                    $inputGroup.find('input.mcb-add-item').on('keydown', function (e) {
                        if (e.which === 13) {
                            let value = $(this).val();
                            if (!value) return false;
                            value = value.split(':');
                            if (value.length === 1) value.push(value[0]);
                            $(this).val('');
                            $inputGroup.before(makeLabel(value[1], value[0])).prev().find('input:checkbox').trigger('click');
                            return false;
                        }
                        return true;
                    });
                    $container.find('.mcb-add-item-button').on('click', function () {
                        const inputId = $(this).attr('data-target-selector');
                        const $input = $(inputId);
                        let value = $input.val();
                        if (!value) return false;
                        value = value.split(':');
                        if (value.length === 1) value.push(value[0]);
                        $input.val('');
                        $inputGroup.before(makeLabel(value[1], value[0])).prev().find('input:checkbox').trigger('click');
                        return false;
                    });
                }

                function makeLabel(label, value) {
                    const tempId = temporaryId();
                    return `
            <div class="form-check form-check-inline">
                <input class="form-check-input" id="${tempId}" type="checkbox" value="${value}">
                <label class="form-check-label" for="${tempId}">${label}</label>
            </div>
        `;
                }

                function sortHashKey(obj, rule) {
                    const keys = Object.keys(obj);
                    if (rule === 'ascend') {
                        keys.sort();
                    } else if (rule === 'descend') {
                        keys.sort().reverse();
                    }
                    return keys;
                }
            });
        },

        /**
         * 入力候補を表示します。
         *
         * Updated: 2020/01/31
         *
         * @param {Object} options
         * @param {Array} options.list 候補になるリスト
         * @param {Boolean} options.hideMaxLengthText (最大文字数: 255)の表記を残したい場合は false
         * @returns {*}
         */
        suggest: function (options) {
            const op = $.extend(
                {
                    list: [],
                    hideMaxLengthText: true
                },
                options
            );

            return this.each(function () {
                const $this = $(this);

                // 最大文字数表記を非表示にする
                if (op.hideMaxLengthText) {
                    $this
                        .next()
                        .filter(function () {
                            return this.innerHTML.indexOf('最大文字数') !== -1;
                        })
                        .hide();
                }

                const suggestionId = temporaryId('suggestion-');
                const completionId = temporaryId('completion-');

                $.data(this, 'suggestList', op.list);
                $this.closest('div.field').css('overflow', 'visible');
                $this.after(
                    '<div class="field-suggestion" id="' +
                        suggestionId +
                        '" style="display:none;">' +
                        '<div class="field_completion" id="' +
                        completionId +
                        '"></div>' +
                        '</div>'
                );

                const outerElm = document.getElementById(suggestionId);
                const innerElm = document.getElementById(completionId);

                $(innerElm).on('click', 'div', function () {
                    const v = $this.val().replace(/(, )?([^,]*)$/, '$1');
                    $this.val(v + $(this).text() + ', ');
                });
                $this.on({
                    blur: function () {
                        setTimeout(function _hide() {
                            outerElm.style.display = 'none';
                        }, 100);
                    },
                    keydown: function (e) {
                        const keycode = e.which || e.keyCode;
                        if (keycode == 13) {
                            const $highlight = $(innerElm).children('.complete-highlight');
                            if ($highlight.length > 0) {
                                const v = $this.val().replace(/(, )?([^,]*)$/, '$1');
                                $this.val(v + $highlight.text() + ', ');
                            }
                            outerElm.style.display = 'none';
                            return false;
                        }
                    },
                    keyup: function (e) {
                        const $highlight = $(innerElm).children('.complete-highlight');
                        switch (e.which) {
                            // down
                            case 40: {
                                if ($highlight[0].nextSibling) {
                                    $highlight
                                        .removeClass('complete-highlight')
                                        .addClass('complete-none')
                                        .next()
                                        .addClass('complete-highlight');
                                }
                                return false;
                            }
                            // up
                            case 38: {
                                if ($highlight[0].previousSibling) {
                                    $highlight
                                        .removeClass('complete-highlight')
                                        .addClass('complete-none')
                                        .prev()
                                        .addClass('complete-highlight');
                                }
                                return false;
                            }
                            default: {
                                const contain = [];
                                let first = true;
                                const v = $(this)
                                    .val()
                                    .match(/(?:, )?([^,]*)$/)[1]
                                    .toLowerCase();
                                if (v !== '') {
                                    for (let i = 0, n = op.list.length; i < n; i++) {
                                        if (op.list[i].toLowerCase().indexOf(v) >= 0) {
                                            if (first) {
                                                contain.push('<div class="complete-highlight">' + op.list[i] + '</div>');
                                                first = false;
                                                outerElm.style.display = 'block';
                                            } else {
                                                contain.push('<div class="complete-none">' + op.list[i] + '</div>');
                                            }
                                        }
                                    }
                                    innerElm.innerHTML = contain.join('');
                                    if (contain.length == 0) {
                                        outerElm.style.display = 'none';
                                    }
                                } else {
                                    outerElm.style.display = 'none';
                                }
                                break;
                            }
                        } // switch
                    }
                });
                return false;
            }); // each
        },

        /**
         * テキストフィールドを項目を自由に増やせるドロップダウンリストに変更することが出来ます。
         *
         * Updated: 2018/06/29
         *
         * @param {Object} options
         * @param {Boolean} options.debug
         * @param {Boolean} options.dynamic
         * @param {String} options.text カンマ区切りの文字列か連想配列と配列の入れ子。value|labelと分けることも可能。
         * @param {String} options.emptyOptionLabel 値が空の選択肢を最初に追加する場合の選択肢のラベル名
         * @param {String} options.addText
         * @param {String} options.promptMsg
         * @param {String} options.initGroupName
         * @param {String} options.selected
         * @returns {*}
         */
        dynamicSelect: function (options) {
            const op = $.extend(
                {
                    debug: false,
                    dynamic: true,
                    text: '',
                    emptyOptionLabel: '',
                    addText: '項目を追加する',
                    promptMsg: '追加する項目名を入力',
                    initGroupName: '選択中アイテム',
                    selected: null
                },
                options
            );

            return this.each(function () {
                const $this = $(this);
                const thisValue = $this.val() || '';
                if (op.debug) {
                    $this.addClass('mb-2');
                    $this.prop('readonly', true);
                    $this.css('background-color', '#F4F4F4');
                } else {
                    $this.hide();
                }

                const options = [];
                if (op.emptyOptionLabel) {
                    options.push(`<option value="">${op.emptyOptionLabel}</option>`);
                }
                let selected = '';
                let exist = false;
                if (typeof op.text == 'string') {
                    const items = op.text.split(',');
                    for (let i = 0, n = items.length; i < n; i++) {
                        const attr = separate(items[i]);
                        if (thisValue == attr[0]) {
                            selected = ' selected="selected"';
                            exist = true;
                        } else {
                            selected = '';
                        }
                        options.push(`<option value="${attr[0]}"${selected}>${attr[1]}</option>`);
                    }
                    if (thisValue && !exist) {
                        options.unshift(`<option value="${thisValue}">${thisValue}</option>`);
                    }
                } else if (typeof op.text == 'object') {
                    for (let key in op.text) {
                        options.push(`<optgroup label="${key}">`);
                        for (let i = 0, n = op.text[key].length; i < n; i++) {
                            const attr = separate(op.text[key][i]);
                            if (thisValue == attr[0]) {
                                selected = ' selected="selected"';
                                exist = true;
                            } else {
                                selected = '';
                            }
                            options.push(`<option value="${attr[0]}"${selected}>${attr[1]}</option>`);
                        }
                        options.push('</optgroup>');
                    }
                    if (thisValue && !exist) {
                        options.unshift(`
                            <optgroup label="${op.initGroupName}">
                                <option value="${thisValue}">${thisValue}</option>
                            </optgroup>
                        `);
                    }
                }
                const option_add = op.dynamic ? `<option value="_add_">${op.addText}</option>` : '';
                const select = ['<select class="dynamic_select form-select">', options.join(''), option_add, '</select>'];
                const $select = $(select.join('')).on('change', function () {
                    if ($(this).val() === '_add_') {
                        const $option = $(this).find('option');
                        const size = $option.size();
                        const addition = prompt(op.promptMsg, '');
                        if (addition) {
                            $this.val(addition);
                            $option.eq(size - 1).before(`<option value="${addition}" selected="selected">${addition}</option>`);
                        } else {
                            $(this).val($this.val());
                        }
                    } else {
                        $this.val($(this).val());
                    }
                    if (op.selected && typeof op.selected === 'function') {
                        op.selected($this.val());
                    }
                    $this.trigger('blur');
                });
                $this.after($select);

                function separate(str) {
                    const array = [];
                    if (str.match(/([^|]+)\|([^|]+)/)) {
                        array[0] = RegExp.$1;
                        array[1] = RegExp.$2;
                    } else {
                        array[0] = str;
                        array[1] = str;
                    }
                    return array;
                }
            });
        },
    };

    $.fn.mtapp = function (method) {
        if (method === 'list') {
            const methodList = ['==================\n.mtapp method list\n=================='];
            for (let key in methods) {
                methodList.push(key);
            }
            console.warn(methodList.join('\n'));
            return true;
        } else if (methods[method]) {
            return methods[method].apply(this, Array.prototype.slice.call(arguments, 1));
        } else if (typeof method === 'object' || !method) {
            return methods.init.apply(this, arguments);
        } else {
            alert('Oops! "' + method + '" does not exist on MTAppjQuery.');
        }
    };
})(jQuery, mtappVars);

/**
 * Utilities
 */
(function ($) {
    $.fn.extend({
        MTAppJSONTable: MTAppJSONTable,
        MTAppListing: MTAppListing,

        /**
         * .hasClasses(classes);
         *
         * `classes` で指定したクラス名が設定されている場合は `true` を返します。
         *
         * Updated: 2018/06/22
         *
         * @param {String} classes チェックするクラス名を半角スペースで区切って指定します。配列で指定することもできます。
         * @returns {boolean}
         */
        hasClasses: function (classes) {
            if (typeof classes === 'string') {
                classes = classes.trim();
                classes = /^\./.test(classes) ? classes.replace(/^\./, '').split('.') : classes.split(/\s+/);
            }
            const count = classes.length;
            let matches = 0;
            for (let i = 0; i < count; i++) {
                if (this.hasClass(classes[i])) {
                    matches++;
                }
            }
            return count === matches;
        },

        /**
         * .notClasses(classes);
         *
         * `classes` で指定したクラス名が設定されていない場合に `true` を返します。
         *
         * Updated: 2018/06/22
         *
         * @param {String} classes チェックするクラス名を半角スペースで区切って指定します。配列で指定することもできます。
         * @returns {boolean}
         */
        notClasses: function (classes) {
            return !this.hasClasses(classes);
        },

        /**
         * .noScroll(styles, containerSelector);
         *
         * 要素をスクロールに追随させます。
         *
         * Updated: 2018/06/22
         *
         * @param {Object} styles .noScroll() を適用した要素に設定するCSSがある場合はObjectで設定する
         * @param {String} containerSelector .noScroll() を適用した要素の親要素のセレクタを指定し、無限スクロールになってしまうのを防ぎます。
         * @returns {*}
         */
        noScroll: function (styles, containerSelector) {
            if (this.length < 1) return;
            if (containerSelector) {
                $(containerSelector).css('overflow-y', 'hidden');
            }
            return this.each(function () {
                const $this = $(this);
                const $parent = $this.parent().css('position', 'relative');
                const parentHeight = $parent.height();
                const $document = $(document);
                const getThisTop = function () {
                    return $document.scrollTop() - $parent.offset().top + 10;
                };
                $parent.height(parentHeight);
                $this.css({ position: 'absolute', 'z-index': 9999 });
                if (styles) {
                    $this.css(styles);
                }
                $(window).scroll(function () {
                    let thisTop = getThisTop();
                    if (thisTop < 0) {
                        thisTop = 0;
                    }
                    if ($document.height() - $document.scrollTop() < $this.height()) {
                        return;
                    }
                    $this.stop().animate({ top: thisTop + 'px' }, 'fast', 'swing');
                });
            });
        },

        /**
         * .insertAtCaret(text);
         *
         * テキストエリアのカーソルの位置に文字列を挿入します。
         *
         * Updated: 2018/06/22
         *
         * @param {String} text カーソルの位置に挿入する文字列を指定します。
         * @returns {*}
         */
        insertAtCaret: function (text) {
            return this.each(function () {
                const self = $(this)[0];
                self.focus();
                if (typeof document.selection !== 'undefined') {
                    const range = document.selection.createRange();
                    range.text = text;
                    range.select();
                } else {
                    const val = self.value;
                    const beforeCaret = self.selectionStart;
                    const afterCaret = beforeCaret + text.length;
                    self.value = val.substr(0, beforeCaret) + text + val.substr(beforeCaret);
                    self.setSelectionRange(afterCaret, afterCaret);
                }
            });
        }
    });
})(jQuery);
